import { useCallback, useState, useMemo, useEffect } from 'react';

import { validateJWT, decodeJWT } from 'Auth/utils/jwt';
import { registerRequestInterceptor, defaultBaseURL } from 'api';

import { useSubscribe } from '@gogame/hooks-ts';
import { useQueryClient } from '@gogame/query';

import { impersonate as impersonateUser } from './sources';

function useSessionStorage(key) {
  const [state, setState] = useState(() => {
    try {
      return sessionStorage.getItem(key) || null;
    } catch (e) {
      return null;
    }
  });
  useMemo(() => {
    sessionStorage.setItem(key, state || '');
  }, [state]);
  return [state, setState];
}

export const useImpersonate = () => {
  const [impersonateToken, setImpersonateToken] =
    useSessionStorage('impersonateToken');
  const queryClient = useQueryClient();
  const isImpersonated = useMemo(() => !!impersonateToken, [impersonateToken]);

  const getSafeImpersonateToken = useCallback(async () => {
    if (isImpersonated) {
      const isValidJWT = validateJWT(impersonateToken);
      if (isValidJWT) {
        return impersonateToken;
      } else {
        await renewImpersonateToken();
      }
    }
  }, [isImpersonated, impersonateToken]);

  const impersonate = useCallback(
    async ({ userId }) => {
      const { token } = await impersonateUser({ userId });
      setImpersonateToken(token);

      return token;
    },
    [impersonateUser, setImpersonateToken]
  );

  const renewImpersonateToken = useCallback(async () => {
    const parsed = decodeJWT(impersonateToken);
    const userId = Number(parsed.sub);
    return await impersonate({ userId });
  }, [impersonateToken, impersonate]);

  const dismissImpersonate = useCallback(() => {
    setImpersonateToken(undefined);
  }, [setImpersonateToken]);

  useSubscribe(() => {
    if (isImpersonated) {
      return registerRequestInterceptor(async (request) => {
        const impersonateToken = await getSafeImpersonateToken();

        if (request?.baseURL === defaultBaseURL) {
          request.headers.set('X-Impersonate-As', impersonateToken);
        }
        return request;
      }, 'ImpersonateToken Request');
    }
  }, [isImpersonated, getSafeImpersonateToken]);

  useEffect(() => {
    return () => {
      queryClient.resetQueries();
    };
  }, [impersonateToken]);

  return {
    isImpersonated: isImpersonated,
    impersonateToken: impersonateToken,
    getImpersonateToken: getSafeImpersonateToken,
    impersonate,
    dismissImpersonate,
  };
};
